import { useState } from 'react';
import { useNavigate, Link, useLocation } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useAuthStore } from '../../../stores/authStore';
import { LoginBackground } from '../../../components/auth/LoginBackground';
import { withAreaPrefix } from '../../../lib/area-routes';
import { NativeButtonWithIcon } from '../../../components/common/NativeButtonWithIcon';

interface RegisterFormData {
  email: string;
  password: string;
  confirmPassword: string;
  name?: string;
}

export function RegisterPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation('auth');
  const { register: registerUser, isLoading } = useAuthStore();
  const [error, setError] = useState<string | null>(null);

  const {
    register,
    handleSubmit,
    watch,
    formState: { errors },
  } = useForm<RegisterFormData>();

  const password = watch('password');

  const onSubmit = async (data: RegisterFormData) => {
    if (data.password !== data.confirmPassword) {
      setError(t('errors.passwords_mismatch'));
      return;
    }

    try {
      setError(null);
      await registerUser(data.email, data.password, data.name);
      navigate(withAreaPrefix('/auth/login', location.pathname));
    } catch (err: unknown) {
      const e = err as { response?: { data?: { message?: string } } };
      setError(e.response?.data?.message || t('errors.registration_failed'));
    }
  };

  return (
    <LoginBackground illustration="sign-up">
      <div className="max-w-md w-full space-y-6 sm:space-y-8 p-6 sm:p-8 bg-violet-500/20 backdrop-blur-xl rounded-2xl shadow-2xl border border-white/20">
        <div className="space-y-3 text-center">
          <h2 className="text-2xl sm:text-3xl font-extrabold tracking-tight text-white">
            {t('register.title')}
          </h2>
          <p className="text-sm text-white/80">{t('register.subtitle')}</p>
        </div>
        <form className="mt-4 sm:mt-6 space-y-6" onSubmit={handleSubmit(onSubmit)}>
          {error && (
            <div className="bg-red-500/30 border border-red-400/50 text-red-100 px-4 py-3 rounded-lg">
              {error}
            </div>
          )}
          <div className="space-y-4">
            <div>
              <label htmlFor="name" className="block text-sm font-medium text-white/90">
                {t('register.name_label')}
              </label>
              <input
                {...register('name')}
                type="text"
                id="name"
                className="mt-1 block w-full px-3 py-2.5 border border-white/30 rounded-xl bg-white/15 text-white placeholder-white/50 focus:outline-none focus:ring-2 focus:ring-white/40 focus:border-white/40"
                placeholder={t('register.name_placeholder')}
              />
            </div>
            <div>
              <label htmlFor="email" className="block text-sm font-medium text-white/90">
                {t('register.email_label')}
              </label>
              <input
                {...register('email', {
                  required: t('validation.email_required'),
                  pattern: {
                    value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                    message: t('validation.invalid_email'),
                  },
                })}
                type="email"
                id="email"
                className="mt-1 block w-full px-3 py-2.5 border border-white/30 rounded-xl bg-white/15 text-white placeholder-white/50 focus:outline-none focus:ring-2 focus:ring-white/40 focus:border-white/40"
                placeholder="you@example.com"
              />
              {errors.email && <p className="mt-1 text-sm text-red-200">{errors.email.message}</p>}
            </div>
            <div>
              <label htmlFor="password" className="block text-sm font-medium text-white/90">
                {t('register.password_label')}
              </label>
              <input
                {...register('password', {
                  required: t('validation.password_required'),
                  minLength: {
                    value: 8,
                    message: t('validation.password_min_length'),
                  },
                })}
                type="password"
                id="password"
                className="mt-1 block w-full px-3 py-2.5 border border-white/30 rounded-xl bg-white/15 text-white placeholder-white/50 focus:outline-none focus:ring-2 focus:ring-white/40 focus:border-white/40"
                placeholder="••••••••"
              />
              {errors.password && (
                <p className="mt-1 text-sm text-red-200">{errors.password.message}</p>
              )}
            </div>
            <div>
              <label htmlFor="confirmPassword" className="block text-sm font-medium text-white/90">
                {t('register.confirm_password_label')}
              </label>
              <input
                {...register('confirmPassword', {
                  required: t('validation.confirm_password_required'),
                  validate: value =>
                    value === password || t('errors.passwords_mismatch'),
                })}
                type="password"
                id="confirmPassword"
                className="mt-1 block w-full px-3 py-2.5 border border-white/30 rounded-xl bg-white/15 text-white placeholder-white/50 focus:outline-none focus:ring-2 focus:ring-white/40 focus:border-white/40"
                placeholder="••••••••"
              />
              {errors.confirmPassword && (
                <p className="mt-1 text-sm text-red-200">{errors.confirmPassword.message}</p>
              )}
            </div>
          </div>

          <div>
            <NativeButtonWithIcon
              type="submit"
              disabled={isLoading}
              icon="user-plus"
              className="w-full flex justify-center py-2.5 px-4 border border-transparent rounded-xl text-sm font-semibold text-violet-900 bg-white hover:bg-white/95 focus:outline-none focus:ring-2 focus:ring-white/50 focus:ring-offset-2 focus:ring-offset-transparent disabled:opacity-60 disabled:cursor-not-allowed transition-colors duration-200 shadow-lg"
            >
              {isLoading ? t('register.submit_button_loading') : t('register.submit_button')}
            </NativeButtonWithIcon>
          </div>

          <div className="text-center text-sm">
            <span className="text-white/80">{t('register.login_link')} </span>
            <Link
              to={withAreaPrefix('/auth/login', location.pathname)}
              className="font-medium text-white hover:text-white/90 transition-colors duration-200 underline underline-offset-2"
            >
              {t('register.login_link_text')}
            </Link>
          </div>
        </form>
      </div>
    </LoginBackground>
  );
}
